import LMSDispatcher from "../LMSDispatcher";

import { IApi, Api } from '../../utilities/Api';
import { ApiRequest } from '../../utilities/ApiRequest';
import * as ACTIONCONTANTS from './ActionConstants';
import TestplanViewData, { ITestplanViewData, defaultTestplanData } from "../../models/testplan/TestplanViewData";
import ITestplanFormData from "../../models/testplan/TestplanFormData";
import { IKeyedInProperty, KeyedInProperty } from "../../models/testplan/TestplanKeyedIn";
import { defaultDirectionData } from "../../models/direction/directionData";
import { IKeyedInPartition, KeyedInPartition } from "../../models/testplan/KeyedInPartition";
import { IKeyedInDirection, KeyedInDirection } from "../../models/testplan/IKeyedInDirection";
import { ITestPlanInstrument, InstrumentData } from "../../models/testplan/TestplanInstrument";
import { ITestPlanDirection, IInstrumentPartition, InstrumentPartition } from "../../models/testplan/InstrumentPartition";
import * as CONSTANTS from "../../utilities/Constants";
import i18next from "i18next";
import ICalculatedPropertyData from "../../models/shared/ICalculatedPropertyData";
import PropertyViewData, { IPropertyViewData } from "../../models/property/PropertyViewData";
import CalculatedPropertyData from '../../models/shared/ICalculatedPropertyData';


class TestplanActions {
    private readonly api: IApi;

    constructor() {
        this.api = new Api(ACTIONCONTANTS.SAMPLE_ENDPOINT);
    }

    async getById(id: string) {
        try {
            const request = new ApiRequest("/testplan/" + id, "", null, i18next.t("Test Plan not found"));
            const result = await this.api.get(request);
            const payload = this.convertResponseData(result.data);
            LMSDispatcher.dispatch({
                type: ACTIONCONTANTS.TESTPLAN_GET_BY_ID,
                payload: {
                    testplanData: payload
                }
            });
        } catch (error) {
            throw error;
        }
    }

    async getAll(status: boolean, page: number, pageSize: number, sort: string, order: string, filter: string) {
        try {
            const request = new ApiRequest(`/testplan/${status}/${page}/${pageSize}/${sort}/${order}/${filter}`, "", null, i18next.t("Internal server error!"));
            const result = await this.api.get(request);
            const payload = this.convertResponseListData(result.data);
            LMSDispatcher.dispatch({
                type: ACTIONCONTANTS.TESTPLAN_GET_ALL,
                payload: {
                    testplanDataList: payload,
                }
            });
        } catch (error) {
            throw error;
        }
    }

    async getAllTp(status: boolean) {
        try {
            const request = new ApiRequest(`/testplan/${status}`, "", null, i18next.t("Internal server error!"));
            const result = await this.api.get(request);
            const payload = this.convertResponseListData(result.data);
            LMSDispatcher.dispatch({
                type: ACTIONCONTANTS.TESTPLAN_GET_ALL,
                payload: {
                    testplanDataList: payload,
                }
            });
        } catch (error) {
            throw error;
        }
    }

    async post(data: ITestplanFormData, selectedInstruments: ITestPlanInstrument[],
        selectedKeyedInProperties: IKeyedInProperty[]): Promise<void> {
        try {
            const payload: any = this.preparePostPayload(data, selectedInstruments, selectedKeyedInProperties);
            const request = new ApiRequest("/testplan", data.requestId, payload, i18next.t("Test Plan created."));
            const result = await this.api.post(request);

            LMSDispatcher.dispatch({
                type: ACTIONCONTANTS.TESTPLAN_SUBMIT,
                payload: {
                    testplanId: result.data,
                    message: result.message
                }
            });
        } catch (error) {
            throw error;
        }
    }

    async put(data: ITestplanFormData, selectedInstruments: ITestPlanInstrument[],
        selectedKeyedInProperties: IKeyedInProperty[]): Promise<void> {
        try {
            const payload: any = this.preparePutPayload(data, selectedInstruments, selectedKeyedInProperties);
            const request = new ApiRequest("/testplan/", data.requestId, payload, i18next.t("Test Plan updated."));
            const result = await this.api.put(request);

            LMSDispatcher.dispatch({
                type: ACTIONCONTANTS.TESTPLAN_SUBMIT,
                payload: {
                    testplanId: data.id,
                    message: result.message
                }
            });
        } catch (error) {
            throw error;
        }
    }

    async deleteMultiple(ids: any[], requestId: string): Promise<void> {
        try {
            const request = new ApiRequest("/testplan", requestId, ids, i18next.t("Test Plan deleted."));
            const result = await this.api.deleteAll(request);
            LMSDispatcher.dispatch({
                type: ACTIONCONTANTS.TESTPLAN_DELETE,
                payload: {
                    reload: true,
                    message: result.message
                }
            });
        } catch (error) {
            throw error;
        }
    }
    async enable(id: string, requestId: string): Promise<void> {
        try {
            const request = new ApiRequest("/testplan/" + id + "/enable", requestId, undefined, i18next.t("Test Plan enabled"));
            const result = await this.api.patch(request);
            LMSDispatcher.dispatch({
                type: ACTIONCONTANTS.TESTPLAN_ENABLE,
                payload: {
                    reload: true,
                    messages: result.message
                }
            });
        } catch (error) {
            throw error;
        }
    }

    async disable(id: string, requestId: string): Promise<void> {
        try {
            const request = new ApiRequest("/testplan/" + id + "/disable", requestId, undefined, i18next.t("Test Plan disabled"));
            const result = await this.api.patch(request);
            LMSDispatcher.dispatch({
                type: ACTIONCONTANTS.TESTPLAN_DISABLE,
                payload: {
                    reload: true,
                    messages: result.message
                }
            });
        } catch (error) {
            throw error;
        }
    }
    private preparePostPayload(data: any, selectedInstruments: ITestPlanInstrument[],
        selectedKeyedInProperties: IKeyedInProperty[]): any {
        const instruments: any[] = [];
        selectedInstruments.forEach((item: ITestPlanInstrument) => {
            const partitions: any[] = [];
            item.partitions.forEach((element: IInstrumentPartition) => {
                const directions: any[] = [];
                element.directions.forEach((d: ITestPlanDirection) => {
                    directions.push({
                        directionId: d.id,
                        count: d.count
                    });
                });

                partitions.push({
                    position: element.index,
                    positionWidth: element.positionWidth,
                    name: element.name,
                    directions
                });
            });

            instruments.push({
                instrumentId: item.id,
                isFixed: item.isFixedPosition,
                fixedWidth: item.fixedPositionWidth,
                partitionsCount: item.partitionsCount,
                stripLength: item.stripLength,
                partitions
            });
        });

        const keyedIns: any[] = [];
        selectedKeyedInProperties.forEach((item: IKeyedInProperty) => {
            const partitions: any[] = [];
            item.partitions.forEach((element: IKeyedInPartition) => {
                const directions: any[] = [];
                element.directions.forEach((d: IKeyedInDirection) => {
                    directions.push({
                        directionId: d.id,
                        count: d.count
                    });
                });

                partitions.push({
                    name: element.name,
                    position: element.index,
                    directions
                });
            });

            keyedIns.push({
                propertyId: item.id,
                partitionsCount: item.partitionsCount,
                partitions
            });
        });

        const calculatedProperties: any[] = [];
        data.properties.forEach((item: any, index: number) => {
            if (item.isCalculated) {
                calculatedProperties.push({
                    propertyId: item.propertyId,
                    target: item.target,
                    sigma: item.sigma.includes("%") ? (parseFloat(item.target) * parseFloat(item.sigma) / 100).toString() : item.sigma,
                    sigmaPercent: item.sigmaPercent ? item.sigmaPercent : false,
                    gain: item.gain ? item.gain : 1,
                    offset: item.offset ? item.offset : 0,
                    minValue: item.minValue,
                    maxValue: item.maxValue,
                    alarmLow1: item.alarmLow1,
                    alarmHigh1: item.alarmHigh1,
                    alarmLow2: item.alarmLow2,
                    alarmHigh2: item.alarmHigh2,
                    rejectLow: item.rejectLow,
                    rejectHigh: item.rejectHigh,
                    formulaExecutionId: "6f8d1410-5f3c-41fc-b619-6bbdc78ffaae",
                    sourceMappings: item.sourceMappings,
                    order: index
                });
            }
        })

        const payloadSourceId = data.isStripLengthFromSource ? data.sourceId : CONSTANTS.DefaultGuid
        return {
            name: data.name,
            description: data.description,
            isPDC: data.isPDC,
            isStripLengthFromSource: data.isStripLengthFromSource,
            sourceId: payloadSourceId,
            instruments,
            keyedIns,
            calculatedProperties: data.showGrade ? [] : calculatedProperties
        };
    }

    private preparePutPayload(data: any, selectedInstruments: ITestPlanInstrument[],
        selectedKeyedInProperties: IKeyedInProperty[]): any {
        const instruments: any[] = [];
        selectedInstruments.forEach((item: ITestPlanInstrument) => {
            const partitions: any[] = [];
            item.partitions.forEach((element: IInstrumentPartition) => {
                const directions: any[] = [];
                element.directions.forEach((d: ITestPlanDirection) => {
                    directions.push({
                        directionId: d.id,
                        count: d.count
                    });
                });

                partitions.push({
                    position: element.index,
                    positionWidth: element.positionWidth,
                    name: element.name,
                    directions
                });
            });

            instruments.push({
                instrumentId: item.id,
                isFixed: item.isFixedPosition,
                fixedWidth: item.fixedPositionWidth,
                partitionsCount: item.partitionsCount,
                stripLength: item.stripLength,
                partitions
            });
        });

        const keyedIns: any[] = [];
        selectedKeyedInProperties.forEach((item: IKeyedInProperty) => {
            const partitions: any[] = [];
            item.partitions.forEach((element: IKeyedInPartition) => {
                const directions: any[] = [];
                element.directions.forEach((d: IKeyedInDirection) => {
                    directions.push({
                        directionId: d.id,
                        count: d.count
                    });
                });

                partitions.push({
                    name: element.name,
                    position: element.index,
                    directions
                });
            });

            keyedIns.push({
                propertyId: item.id,
                partitionsCount: item.partitionsCount,
                partitions
            });
        });

        const calculatedProperties: any[] = [];
        data.properties.forEach((item: any, index: number) => {
            if (item.isCalculated) {
                calculatedProperties.push({
                    propertyId: item.propertyId,
                    target: item.target,
                    sigma: item.sigma.includes("%") ? (parseFloat(item.target) * parseFloat(item.sigma) / 100).toString() : item.sigma,
                    sigmaPercent: item.sigmaPercent ? item.sigmaPercent : false,
                    gain: item.gain ? item.gain : 1,
                    offset: item.offset ? item.offset : 0,
                    minValue: item.minValue,
                    maxValue: item.maxValue,
                    alarmLow1: item.alarmLow1,
                    alarmHigh1: item.alarmHigh1,
                    alarmLow2: item.alarmLow2,
                    alarmHigh2: item.alarmHigh2,
                    rejectLow: item.rejectLow,
                    rejectHigh: item.rejectHigh,
                    formulaExecutionId: "6f8d1410-5f3c-41fc-b619-6bbdc78ffaae",
                    sourceMappings: item.sourceMappings,
                    order: index
                });
            }
        })

        const payloadSourceId = data?.isStripLengthFromSource ? data.sourceId : CONSTANTS.DefaultGuid
        return {
            name: data.name,
            description: data.description,
            id: data.id,
            sourceId: payloadSourceId,
            isPDC: data.isPDC,
            instruments,
            keyedIns,
            calculatedProperties: data.showGrade ? [] : calculatedProperties
        };
    }

    private convertResponseData(data: any): ITestplanViewData {
        const keyedInProperties: IKeyedInProperty[] = [];
        const calculatedProperties: ICalculatedPropertyData[] = [];
        let propertyDetails: IPropertyViewData;
        data.keyedInProperties.forEach((element: any) => {
            const property = new KeyedInProperty(element.property.id, element.property.name, defaultDirectionData);
            property.partitionsCount = element.partitionsCount;
            const partitions: IKeyedInPartition[] = [];

            element.partitions.forEach((item: any, index: any) => {
                const details: IKeyedInDirection[] = [];
                item.details.forEach((t: any) => {
                    details.push(new KeyedInDirection(t.direction.id, t.direction.name, t.count));
                });

                partitions.push(new KeyedInPartition(index, item.name, details));
            });
            property.partitions = partitions;

            keyedInProperties.push(property);
        });

        // Process Instrument Data
        const instruments: ITestPlanInstrument[] = [];
        data.instruments.forEach((element: any) => {
            const instrument = new InstrumentData(element.instrument.id, element.instrument.name, [], (element.isAutoline || element.instrument.isAutoline));
            instrument.partitionsCount = element.partitionsCount;
            instrument.stripLength = element.stripLength;
            instrument.isFixedPosition = element.isFixed;
            instrument.fixedPositionWidth = element.fixedWidth;

            const partitions: IInstrumentPartition[] = [];

            element.partitions.sort((i1: any, i2: any) => {
                return i1.position - i2.position;
            });
            element.partitions.forEach((item: any, index: any) => {
                const directions: ITestPlanDirection[] = [];
                item.details.forEach((t: any) => {
                    directions.push({
                        id: t.direction.id,
                        name: t.direction.name,
                        count: t.count
                    });
                });

                partitions.push(new InstrumentPartition(index, item.positionWidth, directions, 0, item.name, element.isFixed));
            });
            instrument.partitions = partitions;

            instruments.push(instrument);
        });

        //process calculated Data
        if (data.calculatedProperties.length > 0) {
            data.calculatedProperties.forEach((element: any) => {
                const propertyNameDirection = element.property.name;
                propertyDetails = new PropertyViewData(element.property.id, element.property.name, element.property.description,
                    element.property.customId, element.property.type, element.property.typeId, element.property.unit,
                    element.property.decimal, propertyNameDirection)
                calculatedProperties.push(new CalculatedPropertyData(propertyDetails,
                    element.target === null ? "" : element.target,
                    element.sigma === null ? "" : element.sigma, element.gain, element.offset,
                    element.minValue === null || element.minValue === 0 ? "" : element.minValue, element.maxValue === null || element.maxValue === 0 ? "" : element.maxValue,
                    element.alarmLow1 === null ? "" : element.alarmLow1, element.alarmHigh1 === null ? "" : element.alarmHigh1,
                    element.alarmLow2 === null ? "" : element.alarmLow2, element.alarmHigh2 === null ? "" : element.alarmHigh2,
                    element.rejectLow === null ? "" : element.rejectLow,
                    element.rejectHigh === null ? "" : element.rejectHigh,
                    element.sourceMappings, element.formulaMappings, element.order, element.sigmaPercent ? element.sigmaPercent : false))
            });
        }
        return new TestplanViewData(data.id, data.name, data.description, data.codeId, data.isEnabled, instruments, keyedInProperties, data.isPDC, "", [], "", data.sourceId, calculatedProperties, data.isGradeCreatedWithTP);
    }

    private convertResponseListData(data: any) {
        const result: ITestplanViewData[] = [];
        if (data.contents) {
            data.contents.forEach((element: any) => {
                result.push(new TestplanViewData(element.id, element.name, element.description, element.codeId,
                    element.isEnabled, [], [], element.isPDC, element.grade, element.instrumentDetails, element.createdOn, element.sourceId));
            });
        }
        else {
            data.forEach((element: any) => {
                result.push(new TestplanViewData(element.id, element.name, element.description, element.codeId,
                    element.isEnabled, [], [], element.isPDC, element.grade, element.instrumentDetails, element.createdOn, element.sourceId));
            });
        }
        const tempResult = {
            result: data.contents ? result : result,
            currentPage: data.contents ? data.currentPage : 0,
            totalPages: data.contents ? data.totalPages : 0,
            totalRecords: data.contents ? data.totalRecords : 0
        }
        return tempResult;
    }
}

const testplanActions = new TestplanActions();
export default testplanActions;